import { DEVIS_COMPANY, DevisDraft, DevisTotals } from '@/types/devis';

const SERIAL_STORAGE_KEY = 'mk_devis_next_serial';
const HISTORY_STORAGE_KEY = 'mk_devis_history';

export const formatDevisDate = (date = new Date()): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatMoneyTn = (value: number): string => {
  const safe = Number.isFinite(value) ? value : 0;
  return safe.toLocaleString('fr-FR', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

export const formatSerialDisplay = (serial: string): string => {
  const cleaned = serial.replace(/^Devis\s*N[°o.]?\s*/i, '').trim();
  return `Devis N° ${cleaned}`;
};

export const buildSerial = (sequence: number, year = new Date().getFullYear()): string => {
  const padded = String(Math.max(1, Math.floor(sequence))).padStart(5, '0');
  return `${padded}-${year}`;
};

export const parseSerialSequence = (serial: string): number | null => {
  const cleaned = serial.replace(/^Devis\s*N[°o.]?\s*/i, '').trim();
  const match = cleaned.match(/^(\d{1,6})-(\d{4})$/);
  if (!match) return null;
  return Number(match[1]);
};

export const getNextSerialSequence = (): number => {
  if (typeof window === 'undefined') return 1;
  const raw = localStorage.getItem(SERIAL_STORAGE_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

export const setNextSerialSequence = (sequence: number): void => {
  const safe = Math.max(1, Math.floor(sequence));
  localStorage.setItem(SERIAL_STORAGE_KEY, String(safe));
};

export const getSuggestedSerial = (): string => {
  return buildSerial(getNextSerialSequence());
};

/** After a devis is finalized, bump the stored counter past the used serial. */
export const consumeSerialIfNeeded = (serial: string): void => {
  const used = parseSerialSequence(serial);
  if (used == null) return;
  const next = getNextSerialSequence();
  if (used >= next) {
    setNextSerialSequence(used + 1);
  }
};

export const computeDevisTotals = (draft: Pick<DevisDraft, 'items' | 'discount'>): DevisTotals => {
  const totalHt = draft.items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0),
    0
  );
  const discountAmount = Math.min(totalHt, Math.max(0, Number(draft.discount) || 0));
  return {
    totalHt,
    discountAmount,
    totalAfterDiscount: totalHt - discountAmount,
  };
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const buildDevisHtml = (draft: DevisDraft, logoUrl: string): string => {
  const totals = computeDevisTotals(draft);
  const serialLabel = formatSerialDisplay(draft.serialNumber);
  const rows = draft.items
    .map((item) => {
      const designation =
        item.name?.trim() ||
        item.designation.split('\n')[0]?.trim() ||
        'Article';
      const thumb = item.image
        ? `<img src="${escapeHtml(item.image)}" alt="" />`
        : `<div class="thumb-empty"></div>`;

      return `
      <tr>
        <td class="qty">${escapeHtml(String(item.quantity))}</td>
        <td class="thumb">${thumb}</td>
        <td class="designation">${escapeHtml(designation)}</td>
        <td class="price">${formatMoneyTn(item.unit_price)}</td>
        <td class="price">${formatMoneyTn(item.quantity * item.unit_price)}</td>
      </tr>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(serialLabel)}</title>
  <style>
    @page { size: A4; margin: 14mm 12mm; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #111;
      font-size: 12px;
      margin: 0;
      padding: 0;
      background: #fff;
    }
    .sheet {
      width: 100%;
      max-width: 190mm;
      min-height: 269mm;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
    }
    .logo-wrap {
      text-align: center;
      margin-top: 10mm;
      margin-bottom: 14px;
    }
    .logo-wrap img {
      height: 72px;
      width: auto;
      object-fit: contain;
      display: inline-block;
      margin: 0 auto;
    }
    .top {
      display: grid;
      grid-template-columns: 1.15fr auto 1fr;
      gap: 16px;
      align-items: start;
      margin: 0 0 22px;
    }
    .company strong, .client strong { display: block; font-size: 14px; margin-bottom: 4px; }
    .company p, .client p { margin: 2px 0; line-height: 1.35; }
    .client { text-align: right; }
    .client .label { font-weight: 700; text-transform: uppercase; }
    .meta-mid {
      text-align: center;
      padding: 4px 10px;
      min-width: 150px;
    }
    .meta-mid .serial {
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.03em;
      white-space: nowrap;
    }
    .meta-mid .date {
      margin-top: 6px;
      font-size: 12px;
      font-weight: 600;
    }
    table.items {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
    }
    table.items th, table.items td {
      border: 1px solid #222;
      padding: 6px;
      vertical-align: middle;
    }
    table.items th {
      background: #f3f3f3;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }
    .qty { width: 42px; text-align: center; font-weight: 700; }
    .thumb {
      width: 270px;
      text-align: center;
      padding: 8px !important;
    }
    .thumb img {
      width: 250px;
      height: auto;
      object-fit: contain;
      display: block;
      margin: 0 auto;
      border-radius: 4px;
    }
    .thumb-empty {
      width: 250px;
      height: 180px;
      margin: 0 auto;
      background: #f0f0f0;
      border-radius: 4px;
    }
    .designation {
      text-align: left;
      font-weight: 700;
      font-size: 12px;
      vertical-align: top !important;
    }
    .price { width: 95px; text-align: right; white-space: nowrap; }
    .totals {
      margin-top: 16px;
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }
    .totals table { border-collapse: collapse; min-width: 280px; }
    .totals td {
      border: 1px solid #222;
      padding: 8px 10px;
    }
    .totals td.label { font-weight: 700; text-transform: uppercase; background: #f7f7f7; }
    .totals td.value { text-align: right; white-space: nowrap; font-weight: 700; }
    .signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      margin-top: auto;
      margin-bottom: 12mm;
      padding-top: 28px;
      text-align: center;
      font-weight: 700;
      text-transform: uppercase;
      font-size: 12px;
    }
    .signatures .box {
      min-height: 90px;
      border-top: 1px solid #222;
      padding-top: 10px;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="logo-wrap">
      <img src="${escapeHtml(logoUrl)}" alt="Meubles Karim" />
    </div>

    <div class="top">
      <div class="company">
        <strong>${escapeHtml(DEVIS_COMPANY.name)}</strong>
        <p>${escapeHtml(DEVIS_COMPANY.tagline)}</p>
        <p>${escapeHtml(DEVIS_COMPANY.address)}</p>
        <p>${escapeHtml(DEVIS_COMPANY.phone)}</p>
        <p>${escapeHtml(DEVIS_COMPANY.email)}</p>
      </div>
      <div class="meta-mid">
        <div class="serial">${escapeHtml(serialLabel)}</div>
        <div class="date">Date ${escapeHtml(draft.date)}</div>
      </div>
      <div class="client">
        <p><span class="label">Client</span> ${escapeHtml(draft.clientName || '—')}</p>
        <p><span class="label">Adresse</span> ${escapeHtml(draft.clientAddress || '—')}</p>
        ${
          draft.clientMatricule
            ? `<p><span class="label">Matricule F Num</span> ${escapeHtml(draft.clientMatricule)}</p>`
            : ''
        }
      </div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th>Qté</th>
          <th>Photo</th>
          <th>Désignation</th>
          <th>Prix unitaire HT</th>
          <th>Total HT</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="5" style="text-align:center;padding:16px;">Aucun article</td></tr>`}
      </tbody>
    </table>

    <div class="totals">
      <table>
        <tr>
          <td class="label">Total HT</td>
          <td class="value">${formatMoneyTn(totals.totalHt)}</td>
        </tr>
        ${
          totals.discountAmount > 0
            ? `<tr>
          <td class="label">Remise</td>
          <td class="value">- ${formatMoneyTn(totals.discountAmount)}</td>
        </tr>`
            : ''
        }
        <tr>
          <td class="label">Total après remise</td>
          <td class="value">${formatMoneyTn(totals.totalAfterDiscount)}</td>
        </tr>
      </table>
    </div>

    <div class="signatures">
      <div class="box">Décharge client</div>
      <div class="box">Service commercial</div>
    </div>
  </div>
</body>
</html>`;
};

export interface SavedDevisRecord {
  id: string;
  createdAt: string;
  draft: DevisDraft;
  totals: DevisTotals;
}

export const saveDevisToHistory = (draft: DevisDraft): SavedDevisRecord => {
  const record: SavedDevisRecord = {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    draft,
    totals: computeDevisTotals(draft),
  };
  const existing = listDevisHistory();
  localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify([record, ...existing].slice(0, 100)));
  consumeSerialIfNeeded(draft.serialNumber);
  return record;
};

export const listDevisHistory = (): SavedDevisRecord[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const deleteDevisFromHistory = (id: string): void => {
  if (typeof window === 'undefined') return;
  const next = listDevisHistory().filter((record) => record.id !== id);
  localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(next));
};

export const getDevisFromHistory = (id: string): SavedDevisRecord | null => {
  return listDevisHistory().find((record) => record.id === id) || null;
};

export const downloadDevisPdf = async (draft: DevisDraft, logoUrl: string): Promise<void> => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  const html = buildDevisHtml(draft, logoUrl);
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.style.width = '794px';
  host.style.background = '#fff';
  host.innerHTML = html;
  document.body.appendChild(host);

  try {
    const images = Array.from(host.querySelectorAll('img'));
    await Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
      )
    );

    const canvas = await html2canvas(host, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
    });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const fileSerial = draft.serialNumber.replace(/[^\d-]/g, '') || 'devis';
    pdf.save(`DEVIS_${fileSerial}.pdf`);
  } finally {
    document.body.removeChild(host);
  }
};
